import { gridDateComparator, gridNumberComparator } from "@mui/x-data-grid";
import { FieldDef, formatDate, formatDollarAmount } from "@synota-io/synota-shared-ui";
import { ContractStatusBadgeCell } from "./ContractStatusBadgeCell";
import { ContractNameCell } from "./ContractNameCell";
import { IDashboardContract } from "../../../../api/hooks/useContracts";
import { LastInvoiceDateCell } from "./LastInvoiceDateCell";
import { ContractStatusTextCell } from "./ContractStatusTextCell";
import { BooleanCell } from "../../../../shared/cells/BooleanCell";
import { booleanFormatter } from "../../../../utils/formatter";
import { ContractDetailsCell } from "./ContractDetailsCell";
import { SettlementDelayCell } from "./SettlementDelayCell";
import { currentLastEnergyDate } from "../utils/lastEnergyDate";
import { ContractActionsCell } from "./ContractActionsCell";

export type ContractFieldsExtraProps = {
  divider?: boolean; // show dividers on contract details
  role?: "admin" | "user"; // hide everywhere if role is set and doesnt match
};

export const contractFields: FieldDef<IDashboardContract, ContractFieldsExtraProps>[] = [
  {
    field: "utility_service_id",
    headerName: "Utility Service ID",
    width: 170,
  },
  {
    field: "contract_type",
    headerName: "Contract Type",
    displayTags: ["details"],
    role: "admin",
    minWidth: 190,
  },
  {
    field: "contract_state_table",
    displayTags: ["table"],
    headerName: "Status",
    valueGetter: (_value, row) => row.contract_state,
    renderCell: ({ row }) => <ContractStatusBadgeCell contract={row} />,
    width: 60,
  },
  {
    field: "contract_state",
    headerName: "Status",
    displayTags: ["details", "summary"],
    renderCell: ({ row }) => <ContractStatusTextCell contract={row} />,
    minWidth: 120,
  },
  {
    field: "hidden",
    headerName: "Visibility",
    displayTags: ["details"],
    role: "admin",
    valueGetter: (value) => (value ? "Hidden" : "Visible"),
    minWidth: 190,
  },
  {
    field: "contract_name",
    headerName: "Contract Name",
    displayTags: ["table"],
    valueGetter: (_value, row) => row.contract_display_name,
    renderCell: ({ row }) => {
      return <ContractNameCell contract={row} />;
    },
    minWidth: 190,
  },
  {
    field: "begin_time",
    headerName: "Begin Date",
    valueFormatter: (value) => formatDate(value),
    flex: 1,
    minWidth: 100,
  },
  {
    field: "end_time",
    headerName: "Last Energy Date (UTC)",
    valueFormatter: (value) => currentLastEnergyDate(value) || "—",
    displayTags: ["details"],
    flex: 1,
    minWidth: 100,
    divider: true,
  },
  {
    field: "supplier_contract_name",
    headerName: "Supplier Contract Name",
    displayTags: ["details"],
    role: "admin",
    minWidth: 190,
  },
  {
    field: "supplier_name",
    headerName: "Supplier Name",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "supplier_email",
    headerName: "Supplier Email",
    displayTags: ["details"],
    minWidth: 190,
    role: "admin",
  },
  {
    field: "signed_by_supplier",
    headerName: "Supplier Signed",
    displayTags: ["details"],
    role: "admin",
    valueFormatter: booleanFormatter,
    renderCell: BooleanCell,
    minWidth: 190,
    divider: true,
  },
  {
    field: "consumer_contract_name",
    headerName: "Consumer Contract Name",
    displayTags: ["details"],
    role: "admin",
    minWidth: 190,
  },
  {
    field: "consumer_name",
    headerName: "Consumer Name",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "consumer_email",
    headerName: "Consumer Email",
    displayTags: ["details"],
    minWidth: 190,
    role: "admin",
  },
  {
    field: "signed_by_consumer",
    headerName: "Consumer Signed",
    displayTags: ["details"],
    role: "admin",
    valueFormatter: booleanFormatter,
    renderCell: BooleanCell,
    minWidth: 190,
    divider: true,
  },
  {
    field: "payment_method_description",
    headerName: "Payment Method",
    flex: 1,
    minWidth: 100,
    role: "admin",
  },
  {
    field: "receive_method_description",
    headerName: "Receive Method",
    flex: 1,
    minWidth: 100,
    role: "admin",
  },
  {
    field: "advanced_settlement_delay",
    headerName: "Invoice Time",
    renderCell: ({ row }) => <SettlementDelayCell row={row} />,
    role: "admin",
    displayTags: ["details"],
  },
  {
    field: "last_invoice",
    valueFormatter: (value) => formatDate(value),
    renderCell: ({ row, value }) => <LastInvoiceDateCell row={row} value={value} />,
    sortComparator: (a, b, ...params) => {
      if (!a) {
        return 1;
      }
      if (!b) {
        return -1;
      }
      return gridDateComparator(a, b, ...params);
    },
    headerName: "Last Invoice",
    role: "admin",
    flex: 1,
    minWidth: 100,
  },
  {
    field: "amount_outstanding_in_currency",
    headerName: "Outstanding Amount",
    role: "admin",
    valueFormatter: (value) => formatDollarAmount(value),
    sortComparator: gridNumberComparator,
    type: "number",
    flex: 1,
    minWidth: 100,
  },
  {
    field: "actions",
    headerName: "Actions",
    displayTags: ["table"],
    type: "actions",
    renderCell: ({ row }) => <ContractActionsCell contract={row} />,
  },
  {
    field: "details",
    headerName: "Details",
    displayTags: ["table"],
    type: "actions",
    renderCell: ({ row }) => <ContractDetailsCell contract={row} />,
  },
];
